import {
  BooleanField,
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  Edit,
  FilterButton,
  FormDataConsumer,
  FormTab,
  FunctionField,
  List,
  NumberInput,
  required,
  SearchInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
} from "react-admin";
import { useCommonLabels } from "../i18n/hooks";
import { FilePreviewLink, FileUidInput } from "../ui/fileUpload";
import { FormSection } from "../ui/forms/FormSection";
import { DescriptionLangFields, TitleLangFields } from "../ui/forms/langFields";
import { LangTabbedForm } from "../ui/forms/LangTabbedForm";

const SECTION_CHOICES = [
  { id: "helpers", name: "Digər köməkçilər (telefon + xidmətlər)" },
  { id: "more", name: "Daha çox (About, Bloq, Xəbərlər…)" },
];

const ITEM_TYPES = [
  { id: "phone", name: "Telefon — zəng (tel:)" },
  { id: "nav", name: "Naviqasiya — ekran app" },
  { id: "link", name: "Keçid — About, Bloq…" },
  { id: "url", name: "URL — xarici keçid" },
];

const ROUTE_PRESETS = [
  { id: "PatientClinics", name: "PatientClinics — Klinikalar" },
  { id: "PatientDoctors", name: "PatientDoctors — Həkimlər" },
  { id: "PatientConsultations", name: "PatientConsultations — Konsultasiyalar" },
  { id: "About", name: "About — Haqqında" },
  { id: "Blogs", name: "Blogs — Bloq" },
  { id: "News", name: "News — Xəbərlər" },
  { id: "VideoBlogs", name: "VideoBlogs — Media" },
  { id: "Games", name: "Games — Oyunlar" },
  { id: "SafetyMap", name: "SafetyMap — Yardım xəritəsi" },
];

const filters = [
  <SearchInput key="q" source="q" alwaysOn />,
  <SelectInput key="section" source="SectionKey" choices={SECTION_CHOICES} alwaysOn />,
  <SelectInput key="type" source="ItemType" choices={ITEM_TYPES} emptyText="Bütün tiplər" />,
];

const ListActions = ({ section }: { section?: "helpers" | "more" }) => (
  <TopToolbar>
    <FilterButton />
    <CreateButton
      state={
        section
          ? {
              record: {
                SectionKey: section,
                ItemType: section === "more" ? "link" : "phone",
                Active: true,
              },
            }
          : undefined
      }
    />
  </TopToolbar>
);

// Условная обязательность: валидатор смотрит на текущий ItemType,
// поэтому поле не блокирует сохранение, если оно неактуально
// (важно при shouldUnregister=false — поле остаётся зарегистрированным).
const requiredWhen =
  (matches: (all: Record<string, unknown>) => boolean) =>
  (value: unknown, allValues: Record<string, unknown>) =>
    matches(allValues) &&
    (value == null || value === "" || (typeof value === "string" && !value.trim()))
      ? "Обязательное поле"
      : undefined;

const isPhoneItem = (all: Record<string, unknown>) => all?.ItemType === "phone";
const isRouteItem = (all: Record<string, unknown>) =>
  all?.ItemType === "nav" || all?.ItemType === "link";
const isUrlItem = (all: Record<string, unknown>) => all?.ItemType === "url";

const HelperTypeFields = () => (
  <FormDataConsumer>
    {({ formData }) => (
      <>
        {formData?.ItemType === "phone" ? (
          <TextInput
            source="Phone"
            label="Telefon"
            fullWidth
            validate={requiredWhen(isPhoneItem)}
            helperText="Klikdə zəng: 116111, 103"
          />
        ) : null}
        {formData?.ItemType === "nav" || formData?.ItemType === "link" ? (
          <SelectInput
            source="Route"
            label="Marşrut (app ekranı)"
            choices={ROUTE_PRESETS}
            validate={requiredWhen(isRouteItem)}
            fullWidth
          />
        ) : null}
        {formData?.ItemType === "url" ? (
          <TextInput
            source="Route"
            label="URL"
            fullWidth
            validate={requiredWhen(isUrlItem)}
            helperText="Məs: https://116111.az"
          />
        ) : null}
      </>
    )}
  </FormDataConsumer>
);

const PhHelperForm = () => {
  const c = useCommonLabels();
  return (
    <LangTabbedForm>
      <FormTab label="Общее">
        <FormSection title="Тип и настройки">
          <SelectInput
            source="SectionKey"
            label="Bölmə"
            choices={SECTION_CHOICES}
            defaultValue="helpers"
            validate={required()}
            fullWidth
          />
          <SelectInput source="ItemType" label="Tip" choices={ITEM_TYPES} validate={required()} fullWidth />
          <NumberInput source="SortOrder" label="Sıra (yuxarı = əvvəl)" defaultValue={0} fullWidth />
          <BooleanInput source="Active" label={c.active} defaultValue />
        </FormSection>
        <FormSection title="Иконка">
          <FileUidInput
            source="Icon"
            label="İkon"
            kind="image"
            accept="image/*"
            helperText="PNG/SVG — yüklənir fayl servisinə, app göstərir imageUrl"
          />
        </FormSection>
        <FormSection title="Действие">
          <HelperTypeFields />
        </FormSection>
      </FormTab>
      <FormTab label="🇦🇿 AZ">
        <TitleLangFields lang="AZ" titleRequired />
        <FormDataConsumer>
          {({ formData }) =>
            formData?.ItemType === "nav" ? <DescriptionLangFields lang="AZ" rows={2} /> : null
          }
        </FormDataConsumer>
      </FormTab>
      <FormTab label="🇷🇺 RU">
        <TitleLangFields lang="RU" titleRequired />
        <FormDataConsumer>
          {({ formData }) =>
            formData?.ItemType === "nav" ? <DescriptionLangFields lang="RU" rows={2} /> : null
          }
        </FormDataConsumer>
      </FormTab>
      <FormTab label="🇬🇧 EN">
        <TitleLangFields lang="EN" titleRequired />
        <FormDataConsumer>
          {({ formData }) =>
            formData?.ItemType === "nav" ? <DescriptionLangFields lang="EN" rows={2} /> : null
          }
        </FormDataConsumer>
      </FormTab>
    </LangTabbedForm>
  );
};

export const PhHelperList = () => {
  const c = useCommonLabels();
  return (
    <List filters={filters} sort={{ field: "SortOrder", order: "DESC" }} actions={<ListActions />}>
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="SectionKey" label="Bölmə" />
        <TextField source="ItemType" label="Tip" />
        <TextField source="TitleAZ" label="Başlıq (AZ)" />
        <FunctionField
          label="Telefon"
          render={(record: { Phone?: string; ItemType?: string }) =>
            record.ItemType === "phone" ? record.Phone ?? "—" : "—"
          }
        />
        <TextField source="Route" label="Marşrut" />
        <TextField source="SortOrder" label="Sıra" />
        <BooleanField source="Active" label={c.status} />
      </Datagrid>
    </List>
  );
};

export const PhHelperDigarList = () => (
  <List
    resource="phhelper"
    filters={filters}
    filterDefaultValues={{ SectionKey: "helpers" }}
    sort={{ field: "SortOrder", order: "DESC" }}
    title="Digər köməkçilər"
    actions={<ListActions section="helpers" />}
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="ItemType" label="Tip" />
      <TextField source="TitleAZ" label="Başlıq (AZ)" />
      <TextField source="Phone" label="Telefon" />
      <TextField source="Route" label="Marşrut / URL" />
      <FunctionField label="İkon" render={(r: { Icon?: string }) => <FilePreviewLink value={r.Icon} />} />
      <TextField source="SortOrder" label="Sıra" />
      <BooleanField source="Active" label="Aktiv" />
    </Datagrid>
  </List>
);

export const PhHelperMoreList = () => (
  <List
    resource="phhelper"
    filters={filters}
    filterDefaultValues={{ SectionKey: "more" }}
    sort={{ field: "SortOrder", order: "DESC" }}
    title="Daha çox"
    actions={<ListActions section="more" />}
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="TitleAZ" label="Başlıq (AZ)" />
      <TextField source="Route" label="Marşrut / URL" />
      <FunctionField label="İkon" render={(r: { Icon?: string }) => <FilePreviewLink value={r.Icon} />} />
      <TextField source="SortOrder" label="Sıra" />
      <BooleanField source="Active" label="Aktiv" />
    </Datagrid>
  </List>
);

export const PhHelperEdit = () => (
  <Edit mutationMode="pessimistic">
    <PhHelperForm />
  </Edit>
);

export const PhHelperCreate = () => (
  <Create>
    <PhHelperForm />
  </Create>
);
